import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { getAdminShiftPaymentDetails, retryShiftCapture } from "@/lib/payments.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/admin/shifts/$id")({
  head: () => ({ meta: [{ title: "Shift payment details — Admin" }] }),
  component: AdminShiftDetail,
  errorComponent: ({ error }) => <div className="p-8 text-sm text-destructive">{error.message}</div>,
  notFoundComponent: () => <div className="p-8">Shift not found.</div>,
});

async function copy(text: string | null | undefined, label: string) {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch { toast.error("Copy failed"); }
}

function CopyRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="grid grid-cols-[180px_1fr_auto] items-center gap-3 border-t border-border py-2 text-sm first:border-t-0">
      <dt className="text-xs uppercase tracking-wide-luxury text-muted-foreground">{label}</dt>
      <dd className="break-all font-mono text-xs">{value ?? "—"}</dd>
      <Button size="sm" variant="ghost" disabled={!value} onClick={() => copy(value, label)}>Copy</Button>
    </div>
  );
}

function fmtGbp(p: number | null | undefined) {
  return p == null ? "—" : `£${(p / 100).toFixed(2)}`;
}

function AdminShiftDetail() {
  const { id } = Route.useParams();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin-shift-detail", id],
    queryFn: () => getAdminShiftPaymentDetails({ data: { shift_id: id } }),
  });

  const retryMut = useMutation({
    mutationFn: () => retryShiftCapture({ data: { shift_id: id } }),
    onSuccess: (res) => {
      if ("error" in res) { toast.error(res.error); return; }
      toast.success(`Capture ${res.capture_status}.`);
      refetch();
    },
  });

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto max-w-3xl px-6 py-10">
        <p className="text-[11px] tracking-wide-luxury text-muted-foreground">
          <Link to="/admin/reconciliation" className="hover:underline">← Reconciliation</Link>
        </p>
        <h1 className="text-3xl font-light">Shift payment details</h1>

        {isLoading && <p className="mt-6 text-sm text-muted-foreground">Loading…</p>}

        {data && (() => {
          const s = data.shift as Record<string, unknown>;
          const captureStatus = s.capture_status as string | null;
          const canRetry = captureStatus === "failed" || captureStatus === "pending";
          return (
            <>
              <section className="mt-6 border border-border p-6">
                <h2 className="text-lg font-medium">Identifiers</h2>
                <dl className="mt-4">
                  <CopyRow label="Shift ID" value={s.id as string} />
                  <CopyRow label="PaymentIntent ID" value={s.stripe_payment_intent_id as string | null} />
                  <CopyRow label="Checkout Session" value={s.stripe_checkout_session_id as string | null} />
                  <CopyRow label="Capture idempotency key" value={s.capture_idempotency_key as string | null} />
                </dl>
              </section>

              <section className="mt-6 border border-border p-6">
                <h2 className="text-lg font-medium">Capture state</h2>
                <dl className="mt-4 grid grid-cols-2 gap-y-2 text-sm">
                  <dt className="text-muted-foreground">Status</dt>
                  <dd className={`uppercase tracking-wide-luxury ${captureStatus === "failed" ? "text-destructive" : ""}`}>{captureStatus ?? "—"}</dd>
                  <dt className="text-muted-foreground">Captured amount</dt>
                  <dd>{fmtGbp(s.captured_amount_pence as number | null)}</dd>
                  <dt className="text-muted-foreground">Last captured</dt>
                  <dd>{s.last_captured_at ? new Date(s.last_captured_at as string).toLocaleString("en-GB") : "—"}</dd>
                  <dt className="text-muted-foreground">Positions needed</dt>
                  <dd>{s.positions_needed as number}</dd>
                  <dt className="text-muted-foreground">Locked platform fee</dt>
                  <dd>{fmtGbp(s.locked_platform_fee_pence as number | null)}</dd>
                  <dt className="text-muted-foreground">Total cost</dt>
                  <dd>£{Number(s.total_cost ?? 0).toFixed(2)}</dd>
                </dl>
                {s.capture_error ? (
                  <div className="mt-4 rounded border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
                    {s.capture_error as string}
                  </div>
                ) : null}
                {canRetry && (
                  <div className="mt-4">
                    <Button onClick={() => retryMut.mutate()} disabled={retryMut.isPending}>
                      {retryMut.isPending ? "Retrying…" : "Retry capture"}
                    </Button>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Reuses the persisted idempotency key — Stripe will return the original result if a previous capture already succeeded.
                    </p>
                  </div>
                )}
              </section>

              <section className="mt-6 border border-border p-6">
                <h2 className="text-lg font-medium">Bookings</h2>
                <table className="mt-3 w-full text-xs">
                  <thead className="text-left tracking-wide-luxury text-muted-foreground">
                    <tr><th className="py-2">Booking</th><th className="py-2">Bartender</th><th className="py-2">Status</th><th className="py-2">Completed</th></tr>
                  </thead>
                  <tbody>
                    {data.bookings.map((b) => (
                      <tr key={b.id as string} className="border-t border-border">
                        <td className="py-2 font-mono">{(b.id as string).slice(0, 8)}</td>
                        <td className="py-2 font-mono">{((b.bartender_id as string | null) ?? "—").slice(0, 8)}</td>
                        <td className="py-2">{b.status as string}</td>
                        <td className="py-2">{b.completed_at ? new Date(b.completed_at as string).toLocaleString("en-GB") : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            </>
          );
        })()}
      </main>
    </div>
  );
}
